/**
 * Builds a compact, human-friendly label from a value that may or may not be a URL.
 *
 * For URLs, it returns the host's subdomain + domain labels (dropping the TLD and a
 * leading "www"), each capitalized, followed by the last path segment. For example:
 *   https://jira.company.com/browse/ABC-123 -> "Jira Company ABC-123"
 *   https://www.company.com/browse/ABC-123  -> "Company ABC-123"
 *
 * Non-URL or empty values are returned unchanged.
 */
export function getUrlLabel(value: unknown): string {
  if (typeof value !== 'string' || !value) {
    return typeof value === 'string' ? value : '';
  }

  try {
    const { pathname, hostname } = new URL(value);

    const segments = pathname.split('/').filter(Boolean);
    let lastPart = segments[segments.length - 1] ?? '';
    // salesforce append a trailing "/view"
    if (lastPart === 'view' && segments.length > 1) {
      lastPart = segments[segments.length - 2] ?? lastPart;
    }

    const hostLabels = hostname.split('.').filter(Boolean);
    const nameLabels = (hostLabels.length > 1 ? hostLabels.slice(0, -1) : hostLabels).filter(
      (label, index) => !(index === 0 && label === 'www')
    );

    const parts = nameLabels.map((label) => label.charAt(0).toUpperCase() + label.slice(1));
    if (lastPart && !nameLabels.includes(lastPart.toLowerCase())) {
      parts.push(lastPart);
    }

    return parts.join(' ') || value;
  } catch {
    return value;
  }
}
